import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  AtSign,
  FileText,
  Loader2,
  Paperclip,
  Pencil,
  Reply,
  SendHorizontal,
  X,
} from 'lucide-react';
import {
  DISCUSSION_ATTACHMENT_MAX_BYTES,
  DISCUSSION_BODY_MAX_CHARS,
  DISCUSSION_MAX_ATTACHMENTS,
  DISCUSSION_MENTION_ALL_TOKEN,
  discussionAttachmentType,
  type DiscussionAttachmentInput,
  type DiscussionMessage,
} from '@allemnii-pulse/shared';
import { api } from '../../lib/api.js';
import { cn } from '../../lib/utils.js';
import { UserName } from '../../components/UserName.js';
import { plainExcerpt } from './MessageBody.js';

export interface Participant {
  uid: string;
  name: string;
}

export interface ComposedMessage {
  body: string;
  mentionedUids: string[];
  mentionsAll: boolean;
  attachments: DiscussionAttachmentInput[];
  /** Local names and object URLs, only for the optimistic "sending…" copy. */
  localAttachments: { name: string; previewUrl: string | null }[];
}

interface PendingFile {
  key: string;
  file: File;
  previewUrl: string | null;
  status: 'uploading' | 'done' | 'error';
  storagePath?: string;
}

/** An unterminated "@word" right before the caret — what's typed so far filters the picker. */
const MENTION_QUERY_RE = /(?:^|\s)@([\p{L}\p{N}_.-]*)$/u;
const ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.docx,.md,image/*';
/** Phones type a newline with Enter and send with the button; a physical keyboard sends on
 * Enter and takes Shift+Enter for a newline, like every desktop chat. */
const coarsePointer = () => window.matchMedia('(pointer: coarse)').matches;

async function upload(projectId: string, file: File): Promise<string> {
  const { uploadUrl, storagePath, contentType } = await api.post<{
    uploadUrl: string;
    storagePath: string;
    contentType: string;
  }>(`/projects/${projectId}/discussions/attachments/upload-url`, {
    filename: file.name,
    sizeBytes: file.size,
  });
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!res.ok) throw new Error(`Upload failed (${res.status})`);
  return storagePath;
}

/**
 * The message box pinned under the discussion: grows with what you type, offers an @-picker
 * (members and "everyone"), uploads attachments as soon as they're picked so sending is instant,
 * and doubles as the editor for one of your own messages. Replies show the quoted message above.
 */
export function Composer({
  projectId,
  participants,
  replyTo,
  onCancelReply,
  editing,
  onCancelEdit,
  onSubmit,
  onEdit,
  disabledReason,
}: {
  projectId: string;
  participants: Participant[];
  replyTo: DiscussionMessage | null;
  onCancelReply: () => void;
  editing: DiscussionMessage | null;
  onCancelEdit: () => void;
  onSubmit: (message: ComposedMessage) => void;
  onEdit: (
    message: DiscussionMessage,
    edit: Omit<ComposedMessage, 'attachments' | 'localAttachments'>,
  ) => Promise<void>;
  disabledReason?: string | null;
}) {
  const [body, setBody] = useState('');
  const [caret, setCaret] = useState(0);
  const [mentions, setMentions] = useState<{ uid: string; token: string }[]>([]);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [activeOption, setActiveOption] = useState(0);
  const [saving, setSaving] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Entering edit mode loads the message (and its tags) into the box; leaving it clears the box.
  useEffect(() => {
    if (!editing) return;
    setBody(editing.body);
    setMentions(
      editing.mentionedUids.flatMap((uid) => {
        const p = participants.find((x) => x.uid === uid);
        return p ? [{ uid, token: `@${p.name}` }] : [];
      }),
    );
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    });
  }, [editing?.id]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo?.id]);

  // Grow with the content up to about six lines, then scroll inside.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [body]);

  useEffect(
    () => () => files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl)),
    [],
  );

  const mentionQuery = body.slice(0, caret).match(MENTION_QUERY_RE)?.[1] ?? null;
  const options =
    mentionQuery === null
      ? []
      : [
          ...('everyone'.startsWith(mentionQuery.toLowerCase()) ||
          'all'.startsWith(mentionQuery.toLowerCase())
            ? [{ uid: 'all', name: 'everyone' }]
            : []),
          ...participants.filter((p) => p.name.toLowerCase().includes(mentionQuery.toLowerCase())),
        ].slice(0, 7);

  useEffect(() => setActiveOption(0), [mentionQuery]);

  function selectMention(option: Participant) {
    const token = option.uid === 'all' ? DISCUSSION_MENTION_ALL_TOKEN : `@${option.name}`;
    const before = body
      .slice(0, caret)
      .replace(MENTION_QUERY_RE, (m) => `${m.startsWith('@') ? '' : m[0]}${token} `);
    const next = before + body.slice(caret);
    setBody(next);
    if (option.uid !== 'all') {
      setMentions((prev) =>
        prev.some((m) => m.uid === option.uid) ? prev : [...prev, { uid: option.uid, token }],
      );
    }
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      el?.focus();
      el?.setSelectionRange(before.length, before.length);
      setCaret(before.length);
    });
  }

  function insertAt() {
    const el = textareaRef.current;
    const pos = el?.selectionStart ?? body.length;
    const needsSpace = pos > 0 && !/\s/.test(body[pos - 1]!);
    const next = `${body.slice(0, pos)}${needsSpace ? ' ' : ''}@${body.slice(pos)}`;
    const nextPos = pos + (needsSpace ? 2 : 1);
    setBody(next);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(nextPos, nextPos);
      setCaret(nextPos);
    });
  }

  function pickFiles(list: FileList | null) {
    if (!list) return;
    setError(null);
    const picked = [...list];
    const room = DISCUSSION_MAX_ATTACHMENTS - files.length;
    if (picked.length > room) setError(`Up to ${DISCUSSION_MAX_ATTACHMENTS} files per message.`);
    const accepted: PendingFile[] = [];
    for (const file of picked.slice(0, Math.max(room, 0))) {
      const type = discussionAttachmentType(file.name);
      if (!type) {
        setError(`"${file.name}" can't be attached — images, PDF, .docx and .md only.`);
        continue;
      }
      if (file.size > DISCUSSION_ATTACHMENT_MAX_BYTES) {
        setError(`"${file.name}" is over the 25 MB limit.`);
        continue;
      }
      accepted.push({
        key: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        file,
        previewUrl: type.kind === 'image' ? URL.createObjectURL(file) : null,
        status: 'uploading',
      });
    }
    setFiles((prev) => [...prev, ...accepted]);
    for (const item of accepted) {
      upload(projectId, item.file)
        .then((storagePath) =>
          setFiles((prev) =>
            prev.map((f) => (f.key === item.key ? { ...f, status: 'done', storagePath } : f)),
          ),
        )
        .catch(() =>
          setFiles((prev) => prev.map((f) => (f.key === item.key ? { ...f, status: 'error' } : f))),
        );
    }
  }

  function removeFile(key: string) {
    setFiles((prev) => {
      const gone = prev.find((f) => f.key === key);
      if (gone?.previewUrl) URL.revokeObjectURL(gone.previewUrl);
      return prev.filter((f) => f.key !== key);
    });
  }

  const trimmed = body.trim();
  const uploading = files.some((f) => f.status === 'uploading');
  const failed = files.some((f) => f.status === 'error');
  const canSend = editing
    ? !saving && (trimmed.length > 0 || editing.attachments.length > 0)
    : !uploading && !failed && (trimmed.length > 0 || files.length > 0);

  function tags() {
    return {
      mentionedUids: mentions.filter((m) => trimmed.includes(m.token)).map((m) => m.uid),
      mentionsAll: trimmed.includes(DISCUSSION_MENTION_ALL_TOKEN),
    };
  }

  async function submit() {
    if (!canSend) return;
    if (editing) {
      setSaving(true);
      try {
        await onEdit(editing, { body: trimmed, ...tags() });
        reset();
        onCancelEdit();
      } catch {
        setError("Couldn't save your edit. Try again.");
      } finally {
        setSaving(false);
      }
      return;
    }
    onSubmit({
      body: trimmed,
      ...tags(),
      attachments: files.map((f) => ({ storagePath: f.storagePath!, name: f.file.name })),
      localAttachments: files.map((f) => ({ name: f.file.name, previewUrl: f.previewUrl })),
    });
    // Object URLs now belong to the optimistic message; the page revokes them once it's sent.
    setFiles([]);
    reset();
  }

  function reset() {
    setBody('');
    setMentions([]);
    setError(null);
    setCaret(0);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (options.length > 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const step = e.key === 'ArrowDown' ? 1 : -1;
        setActiveOption((i) => (i + step + options.length) % options.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        selectMention(options[activeOption]!);
        return;
      }
    }
    if (e.key === 'Escape' && (editing || replyTo)) {
      e.preventDefault();
      if (editing) {
        reset();
        onCancelEdit();
      } else onCancelReply();
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !coarsePointer()) {
      e.preventDefault();
      void submit();
    }
  }

  if (disabledReason) {
    return (
      <div className="border-t border-border bg-surface px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] text-center text-sm text-muted">
        {disabledReason}
      </div>
    );
  }

  const quoted = editing ?? replyTo;
  return (
    <div className="relative flex-shrink-0 border-t border-border bg-surface px-3 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 sm:px-4">
      {options.length > 0 && (
        <div
          role="listbox"
          aria-label="Tag someone"
          className="absolute inset-x-3 bottom-full mb-1 max-h-64 overflow-y-auto rounded-xl border border-border bg-surface p-1 shadow-lg sm:inset-x-4 sm:max-w-xs"
        >
          {options.map((option, i) => (
            <button
              key={option.uid}
              type="button"
              role="option"
              aria-selected={i === activeOption}
              // pointerdown (not click) fires before the textarea loses focus, so the keyboard
              // stays up on a phone and the caret is still where the "@" was typed.
              onPointerDown={(e) => {
                e.preventDefault();
                selectMention(option);
              }}
              className={cn(
                'flex h-11 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[15px] md:h-9 md:text-sm',
                i === activeOption ? 'bg-surface-2' : 'hover:bg-surface-2',
              )}
            >
              {option.uid === 'all' ? (
                <>
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-accent-soft text-accent-soft-fg">
                    <AtSign className="h-3.5 w-3.5" />
                  </span>
                  <span>
                    <span className="font-medium">everyone</span>
                    <span className="ml-1.5 text-xs text-muted">Notify all members</span>
                  </span>
                </>
              ) : (
                <>
                  <UserName
                    uid={option.uid}
                    avatar
                    hideName
                    avatarClassName="h-6 w-6"
                    showStatus={false}
                  />
                  <span className="truncate">{option.name}</span>
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {quoted && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border-l-2 border-accent bg-surface-2 py-1.5 pl-2.5 pr-1 text-xs">
          {editing ? (
            <Pencil className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
          ) : (
            <Reply className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
          )}
          <div className="min-w-0 flex-1">
            <span className="font-medium text-accent">
              {editing ? (
                'Editing your message'
              ) : (
                <>
                  Replying to <UserName uid={quoted.authorUid} showStatus={false} />
                </>
              )}
            </span>
            {!editing && <p className="truncate text-muted">{plainExcerpt(quoted)}</p>}
          </div>
          <button
            type="button"
            aria-label={editing ? 'Cancel editing' : 'Cancel reply'}
            onClick={() => {
              if (editing) {
                reset();
                onCancelEdit();
              } else onCancelReply();
            }}
            className="-my-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted hover:text-text"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {files.length > 0 && (
        <div className="mb-2 flex gap-2 overflow-x-auto pb-1">
          {files.map((f) => (
            <div
              key={f.key}
              className={cn(
                'relative flex h-16 shrink-0 items-center overflow-hidden rounded-lg border bg-surface-2',
                f.status === 'error' ? 'border-danger' : 'border-border',
                f.previewUrl ? 'w-16' : 'w-44 gap-2 px-2',
              )}
            >
              {f.previewUrl ? (
                <img src={f.previewUrl} alt={f.file.name} className="h-full w-full object-cover" />
              ) : (
                <>
                  <FileText className="h-5 w-5 shrink-0 text-accent" />
                  <span className="min-w-0 truncate text-xs">{f.file.name}</span>
                </>
              )}
              {f.status === 'uploading' && (
                <span className="absolute inset-0 flex items-center justify-center bg-black/30 text-white">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </span>
              )}
              <button
                type="button"
                aria-label={`Remove ${f.file.name}`}
                onClick={() => removeFile(f.key)}
                className="absolute right-0.5 top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {(error || failed) && (
        <p className="mb-1.5 text-xs text-danger">
          {error ?? 'A file failed to upload — remove it and try again.'}
        </p>
      )}

      <div className="flex items-end gap-1.5">
        {!editing && (
          <>
            <button
              type="button"
              aria-label="Attach files"
              title="Attach images, PDF, .docx or .md"
              disabled={files.length >= DISCUSSION_MAX_ATTACHMENTS}
              onClick={() => fileInputRef.current?.click()}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-text disabled:opacity-40"
            >
              <Paperclip className="h-5 w-5" />
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => {
                pickFiles(e.target.files);
                e.target.value = '';
              }}
            />
          </>
        )}
        <button
          type="button"
          aria-label="Tag someone"
          onPointerDown={(e) => {
            e.preventDefault();
            insertAt();
          }}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-muted hover:bg-surface-2 hover:text-text md:hidden"
        >
          <AtSign className="h-5 w-5" />
        </button>
        <textarea
          ref={textareaRef}
          value={body}
          rows={1}
          maxLength={DISCUSSION_BODY_MAX_CHARS}
          placeholder={editing ? 'Edit your message' : 'Message the team'}
          aria-label="Message"
          enterKeyHint={coarsePointer() ? 'enter' : 'send'}
          onChange={(e) => {
            setBody(e.target.value);
            setCaret(e.target.selectionStart);
          }}
          onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            if (editing || e.clipboardData.files.length === 0) return;
            e.preventDefault();
            pickFiles(e.clipboardData.files);
          }}
          className="max-h-40 min-h-10 min-w-0 flex-1 resize-none rounded-2xl border border-border bg-bg px-3.5 py-2 text-sm leading-6 outline-none placeholder:text-muted focus-visible:border-accent"
        />
        <button
          type="button"
          aria-label={editing ? 'Save edit' : 'Send'}
          disabled={!canSend}
          onClick={() => void submit()}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent text-accent-fg transition-opacity disabled:opacity-40"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <SendHorizontal className="h-[18px] w-[18px]" />
          )}
        </button>
      </div>
    </div>
  );
}
